import {
  fetchRegionFAQ,
  fetchRegionFeatured, fetchRegionMeta, fetchRegionRelated, fetchRegionRelatedNews,
  fetchRegionResortListGuide,
  fetchRegionResortTopRating,
} from 'api/region/static';
import {
  IRegionFAQ,
  IRegionFeaturedResort, IRegionShort,
} from 'api/region/types';
import { AxiosResponse } from 'axios';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import { CurrentLink, CurrentTab } from 'components/region/RegionHeaderNav/constants';
import { Main } from 'components/resort/Layout/Main';
import { Side } from 'components/resort/Layout/Side';
import { SidebarAd } from 'googlead/components/SidebarAd';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { IRegionLayout } from 'page-components/region/_layout';

import React, { useRef } from 'react';

import { getRegionSlug } from 'util/memcache';
import { useMustacheTranslation, useOTSTranslation } from 'i18n/useOTSTranslation';
import { IResortTopRated, ResortTopRated } from 'api/home/types';
import { IRelatedNews, IResortPathInfo } from 'api/resort/types';

import { BestResorts } from 'components/home/BestResorts';
import { AllResorts } from 'components/region/AllResorts';
import { RegionFAQ } from 'components/region/RegionFAQ';
import { RegularAd } from 'googlead/components/RegularAd';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { BlockContent } from 'components/blocks/BlockContent';
import PlanYourTrip from 'components/common/PlanYourTrip/RegionWidget';
import { LdJsonRegionOverview } from 'components/LdJson';
import { isMainLocale } from 'util/i18nFlags';
import { Hero400Error } from 'components/400';
import SurfaceRelatedArticles from 'components/resort/SurfaceRelatedArticles';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';

export type RegionGuidePageProps = {
  featured: IRegionFeaturedResort,
  regionResortTopRatingOverall: Array<IResortTopRated>,
  regionResortListGuide: Array<IResortPathInfo>,
  regionFAQ: IRegionFAQ,
  pathInfo: IRegionShort,
  numResorts: number,
  relatedNews: IRelatedNews | null,
} & RouteAndAnalyticsProps & IRegionLayout;

export const DEBUG_TAG = 'region-ski-resorts-page';
export const TEMPLATE_ID = '3.1.1';
export const TEMPLATE_NAME = 'Region Guide Page';

export default function RegionGuidePage({
  regionResortTopRatingOverall, pathInfo, regionResortListGuide, regionFAQ, numResorts,
  relatedNews,
}: RegionGuidePageProps) {
  const refScroll = useRef<HTMLDivElement>(null);
  const { t } = useOTSTranslation('common');
  const m = useMustacheTranslation('common');
  const { t: seoT } = useOTSTranslation('seo');

  const showLodgingWidget = isMainLocale();

  if (!regionFAQ || !regionResortListGuide || !regionResortTopRatingOverall) {
    return <Hero400Error light />;
  }

  const seoTitle = pathInfo.isLogical ? t('region.resortGuide.logicSeoTitle', { region: pathInfo.title })
    : t('region.resortGuide.seoTitle', { region: pathInfo.title });
  const seoText = pathInfo.isLogical ? m('region.resortGuide.logicSeoText', {
    region: pathInfo.title,
    numresorts: numResorts,
  }) : m('region.resortGuide.seoText', {
    region: pathInfo.title,
    numresorts: numResorts,
  });

  const regionSeoDescription = seoT('MetaRegion.profile.description', { region: pathInfo.title });

  return (
    <>
      <Main>
        <BestResorts
          resortTopRatingOverall={regionResortTopRatingOverall}
          regionUUID={pathInfo.uuid}
          regionTitle={pathInfo.title}
          isFullWidth
        />
        <BoxWithShadow>
          <BlockHeader title={seoTitle} />
          <BlockContent>
            {seoText}
          </BlockContent>
        </BoxWithShadow>
        <div className="md-hide m-0">
          <AllResorts
            regionResortListGuide={regionResortListGuide}
          />
        </div>
        {showLodgingWidget && (
          <PlanYourTrip
            region={pathInfo}
            hasSkiRental={false}
            hasCarRental={false}
            hasFlights={false}
            isPartner={false}
          />
        )}
        {regionFAQ && (
          <RegionFAQ
            regionTitle={pathInfo.title}
            regionFAQ={regionFAQ}
            refScroll={refScroll}
            placeholders={{
              region: pathInfo.title,
              NUMRESORTS: String(numResorts),
              numresorts: String(numResorts),
            }}
          />
        )}
        <div className="md-show m-0">
          <AllResorts
            regionResortListGuide={regionResortListGuide}
          />
        </div>
        <RegularAd isFirst />
      </Main>
      <Side>
        <SurfaceRelatedArticles
          relatedNewsList={relatedNews}
          isFresh
        />
        <SidebarAd isRegion id={pathInfo.uuid} />
      </Side>
      <LdJsonRegionOverview
        region={pathInfo}
        resorts={regionResortListGuide}
        topRatedResorts={regionResortTopRatingOverall}
        numResorts={numResorts}
        description={regionSeoDescription}
      />
    </>
  );
}

export const fetchProps = async ({ region }:{ region: string }): Promise<RegionGuidePageProps> => {
  const cache: AxiosResponse<IRegionShort> = await assertShouldBeNot404(() => getRegionSlug(region));
  const { uuid } = cache.data;
  const featured = await fetchRegionFeatured(uuid);
  const regionResortTopRatingOverall = await fetchRegionResortTopRating(uuid, ResortTopRated.overall);
  const regionResortListGuide = await fetchRegionResortListGuide(uuid);
  const regionFAQ = await fetchRegionFAQ(uuid);

  return {
    type: 'region',
    route: 'ski-resorts',
    featured,
    regionResortTopRatingOverall,
    regionResortListGuide,
    regionFAQ,
    pathInfo: cache.data,
    numResorts: (await fetchRegionMeta(uuid))?.resorts?.active,
    relatedNews: await fetchRegionRelatedNews(uuid),

    /**
       * Layout
       */
    related: await fetchRegionRelated(uuid),
    navigation: {
      head: HeaderNavCurLink.TRIP_PLANNING,
      tab: CurrentTab.TRIP_PLANNING,
      link: CurrentLink.OVERVIEW,
    },
    adTKey: 'metaregion travel',
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    hero: {
      title: 'region.resortGuide.heroTitle',
    },
    meta: {
      redirectTo: cache.data.domain ?? null,
      description: 'MetaRegion.profile.description',
      title: 'MetaRegion.profile.title',
    },
    prefetch: false,
  };
};

// This function gets called at build time
// eslint-disable-next-line max-len
export const getRegionGuidePageStaticProps = wrapStaticProps(fetchProps, false);

export const getRegionGuidePageServerSideProps = wrapServerSideProps(fetchProps, false);
